import React, { useEffect, useMemo, useRef, useState } from "react";
import { Store, Search, ChevronRight, Check } from "lucide-react";

const ParticipatingStoresSelector = ({
  businesses,
  selectedStores,
  onChangeSelectedStores,
  onCommitSelectedStores,
}) => {
  const containerRef = useRef(null);
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");

  const filteredStores = useMemo(
    () =>
      businesses
        .filter((store) =>
          store.name.toLowerCase().includes(storeSearch.toLowerCase()),
        )
        .map((store) => ({
          id: store.id,
          name: store.name,
        })),
    [businesses, storeSearch],
  );

  const handleToggleStore = (storeId) => {
    onChangeSelectedStores((previous) => {
      if (previous.includes(storeId)) {
        return previous.filter((item) => item !== storeId);
      }
      return [...previous, storeId];
    });
  };

  const handleSelectAllStores = () => {
    const nextSelection = [...selectedStores];

    filteredStores.forEach((store) => {
      if (!nextSelection.includes(store.id)) {
        nextSelection.push(store.id);
      }
    });

    onChangeSelectedStores(nextSelection);
  };

  const handleSelectNoStores = () => {
    onChangeSelectedStores([]);
  };

  const handleCloseDropdown = () => {
    setIsStoreDropdownOpen(false);
    if (onCommitSelectedStores) {
      onCommitSelectedStores(selectedStores);
    }
  };

  const handleToggleDropdown = () => {
    if (isStoreDropdownOpen) {
      handleCloseDropdown();
      return;
    }
    setIsStoreDropdownOpen(true);
  };

  useEffect(() => {
    if (!isStoreDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      if (container.contains(event.target)) {
        return;
      }

      handleCloseDropdown();
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      handleCloseDropdown();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isStoreDropdownOpen, selectedStores]);

  const totalStores = businesses.length || 0;
  const allSelected = selectedStores.length === totalStores && totalStores > 0;

  return (
    <div>
      <label className="mb-1.5 block flex justify-between text-sm font-medium text-gray-300">
        <span>Participating Stores</span>
        <span className="text-xs text-gray-400">
          {allSelected
            ? "All stores selected"
            : `${selectedStores.length} stores selected`}
        </span>
      </label>

      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={handleToggleDropdown}
          className="flex w-full items-center justify-between rounded-lg border border-gray-800 bg-gray-950 px-4 py-3 text-left transition-colors hover:border-gray-700"
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <Store size={16} className="flex-shrink-0 text-gray-500" />
            {selectedStores.length > 0 ? (
              <div className="flex gap-1 overflow-hidden">
                {allSelected ? (
                  <span className="text-sm font-medium text-blue-400">
                    All Locations Selected
                  </span>
                ) : (
                  selectedStores.slice(0, 2).map((storeId) => (
                    <span
                      key={storeId}
                      className="rounded border border-blue-500/30 bg-blue-950/40 px-2 py-0.5 text-xs whitespace-nowrap text-blue-300"
                    >
                      {businesses
                        .find((business) => business.id === storeId)
                        ?.name?.split(" ")
                        .slice(0, 2)
                        .join(" ")}
                      ...
                    </span>
                  ))
                )}
                {selectedStores.length > 2 && !allSelected && (
                  <span className="ml-1 py-0.5 text-xs text-gray-400">
                    +{selectedStores.length - 2} more
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm text-gray-500">
                Select stores that accept this discount...
              </span>
            )}
          </div>
          <ChevronRight
            size={16}
            className={`text-gray-500 transition-transform ${
              isStoreDropdownOpen ? "rotate-90" : ""
            }`}
          />
        </button>

        {isStoreDropdownOpen && (
          <div className="animate-fade-in absolute top-full right-0 left-0 z-20 mt-2 flex max-h-96 flex-col overflow-hidden rounded-xl border border-gray-800 bg-gray-900 shadow-xl">
            <div className="sticky top-0 z-10 border-b border-gray-800 bg-gray-950 p-3">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-500"
                />
                <input
                  type="text"
                  value={storeSearch}
                  onChange={(event) => setStoreSearch(event.target.value)}
                  placeholder="Search stores..."
                  className="w-full rounded-lg border border-gray-800 bg-gray-900 py-2 pr-3 pl-9 text-xs text-white placeholder-gray-500 outline-none focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex shrink-0 items-center justify-between border-b border-gray-800 bg-gray-900/80 px-3 py-2">
              <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
                {filteredStores.length} Locations
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllStores}
                  className="rounded bg-blue-500/10 px-2 py-1 text-[10px] text-blue-400 transition-colors hover:bg-blue-500/20"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleSelectNoStores}
                  className="rounded bg-gray-800 px-2 py-1 text-[10px] text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                >
                  None
                </button>
              </div>
            </div>

            <div className="min-h-[150px] flex-1 overflow-y-auto p-1">
              {filteredStores.length > 0 ? (
                filteredStores.map((store) => {
                  const isSelected = selectedStores.includes(store.id);

                  return (
                    <button
                      key={store.id}
                      type="button"
                      onClick={() => handleToggleStore(store.id)}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-lg p-2.5 text-left transition-colors ${
                        isSelected
                          ? "bg-blue-600/10 hover:bg-blue-600/20"
                          : "hover:bg-gray-800"
                      }`}
                    >
                      <div
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                          isSelected
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-600 bg-gray-950"
                        }`}
                      >
                        {isSelected && (
                          <Check size={12} className="text-white" />
                        )}
                      </div>
                      <span
                        className={`text-sm leading-tight ${
                          isSelected ? "text-white" : "text-gray-300"
                        }`}
                      >
                        {store.name}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="p-4 text-center text-xs text-gray-500">
                  No stores found matching &quot;{storeSearch}&quot;
                </div>
              )}
            </div>

            <div className="sticky bottom-0 z-10 border-t border-gray-800 bg-gray-950 p-2">
              <button
                type="button"
                onClick={handleCloseDropdown}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 text-xs font-bold text-white shadow-lg shadow-blue-500/20 transition-colors hover:bg-blue-500"
              >
                <Check size={14} />
                Save Selection ({selectedStores.length})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipatingStoresSelector;
